import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  downloadAdminDatabaseBackup,
  fetchAdminSecurityModule12,
  restoreAdminDatabaseBackup,
  restoreAdminDatabaseBackupFromUpload,
  runAdminDatabaseBackup,
} from '../../../services/academiaAdminService';
import { ADMIN_TABLE_PAGE_SIZE } from '../../../hooks/useAdminTablePagination';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';
import Module12DataPurgeSection from './Module12DataPurgeSection';
import { isAdministrateur } from '../../../utils/adminAccess';

const AUDIT_PAGE_SIZE = ADMIN_TABLE_PAGE_SIZE;

type Module12Data = {
  periodeJours?: number;
  authentification?: {
    jwtAccessExpiry?: string;
    jwtRefreshExpiry?: string;
    jwtSecretsConfigured?: boolean;
    bcryptRounds?: number;
    otpEnabled?: boolean;
    otpVerifications30j?: number;
    twoFactorEnabled?: boolean;
    helmetActif?: boolean;
    middlewareAuth?: boolean;
  };
  chiffrement?: {
    motsDePasseHash?: boolean;
    algorithme?: string;
    jwtSigne?: boolean;
    transitHttpsRecommande?: boolean;
    baseDonneesTls?: boolean;
    journalDetailsChiffres?: boolean;
    algorithmeJournaux?: string | null;
  };
  sauvegardes?: {
    actif?: boolean;
    planification?: string;
    retentionJours?: number;
    stockage?: string | null;
    message?: string;
    derniereReussite?: string | null;
    derniereErreur?: boolean | null;
    fichiers?: number;
    dernierFichier?: string | null;
    inventaireFichiers?: Array<{
      nom: string;
      tailleLisible?: string | null;
      dateModification?: string | null;
      existe?: boolean;
    }>;
  };
  journalisation?: {
    console?: boolean;
    fichier?: boolean;
    repertoire?: string | null;
  };
  roles?: {
    totalUtilisateurs?: number;
    administrateursActifs?: number;
    repartition?: Array<{ role: string; label: string; count: number }>;
  };
  volumes30j?: {
    entreesAudit?: number;
    verificationsOtp?: number;
  };
  couvertureFiche?: Record<string, boolean>;
  journalAudit?: {
    items?: Array<{
      id: string;
      when: string;
      who: string;
      action: string;
      details?: string | null;
      danger?: boolean;
    }>;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
};

const FICHE_LABELS: Record<string, string> = {
  authentificationSecurisee: 'Connexion sécurisée',
  gestionRoles: 'Gestion des profils',
  chiffrementDonnees: 'Protection des données',
  journalisation: 'Enregistrement des activités',
  sauvegardesAutomatiques: 'Sauvegardes automatiques',
  auditOperations: 'Suivi des actions administratives',
};

function formatTokenDuration(raw?: string): string {
  if (!raw) return '—';
  const match = /^(\d+)([mhd])$/i.exec(raw.trim());
  if (!match) return raw;
  const value = Number(match[1]);
  const unit = match[2].toLowerCase();
  if (unit === 'm') return `${value} min`;
  if (unit === 'h') return `${value} h`;
  return `${value} jour${value > 1 ? 's' : ''}`;
}

function formatBackupSchedule(cron?: string): string {
  if (!cron?.trim()) return 'Non planifiée';
  const parts = cron.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0] !== '*' && parts[1] !== '*' && parts[2] === '*') {
    const hour = parts[1].padStart(2, '0');
    const minute = parts[0].padStart(2, '0');
    return `Tous les jours à ${hour}:${minute}`;
  }
  return 'Planification personnalisée';
}

type KpiTone = 'primary' | 'sky' | 'emerald' | 'amber';

const KPI_TONE_CLASS: Record<KpiTone, string> = {
  primary: 'border-outline-variant/25 bg-gradient-to-br from-white to-surface-container-low',
  sky: 'border-sky-200/50 bg-gradient-to-br from-sky-50/80 to-white',
  emerald: 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/70 to-white',
  amber: 'border-amber-200/50 bg-gradient-to-br from-amber-50/70 to-white',
};

const KPI_LABEL_CLASS: Record<KpiTone, string> = {
  primary: 'text-on-surface-variant',
  sky: 'text-sky-800',
  emerald: 'text-emerald-800',
  amber: 'text-amber-800',
};

const KPI_VALUE_CLASS: Record<KpiTone, string> = {
  primary: 'text-primary',
  sky: 'text-sky-900',
  emerald: 'text-emerald-900',
  amber: 'text-amber-900',
};

function KpiTile({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: string;
  tone?: KpiTone;
}) {
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${KPI_TONE_CLASS[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <p className={`text-xs font-bold uppercase tracking-wide ${KPI_LABEL_CLASS[tone]}`}>{label}</p>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/80 text-primary shadow-sm">
          <LucideSymbolIcon name={icon} tone="inherit" size={18} />
        </span>
      </div>
      <p className={`mt-2 text-3xl font-black tabular-nums ${KPI_VALUE_CLASS[tone]}`}>{value}</p>
      {hint ? <p className={`mt-1 text-xs ${KPI_LABEL_CLASS[tone]}`}>{hint}</p> : null}
    </article>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'academic-panel-badge academic-panel-badge--success'
          : 'academic-panel-badge academic-panel-badge--muted'
      }
    >
      {active ? 'Actif' : 'À configurer'}
    </span>
  );
}

function formatShortDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatAuditDetailsPreview(details?: string | null) {
  if (!details?.trim()) return '—';
  if (details.trim().startsWith('{')) return 'Détails complets disponibles';
  return details.length > 100 ? `${details.slice(0, 100)}…` : details;
}

const RESTORE_CONFIRMATION = 'RESTAURER';

type ToastState = { message: string; variant: 'success' | 'error' };

type SecurityTabId = 'overview' | 'protection' | 'backups' | 'audit' | 'purge';

const SECURITY_TABS: { id: SecurityTabId; label: string; icon: string; adminOnly?: boolean }[] = [
  { id: 'overview', label: 'Vue d’ensemble', icon: 'dashboard' },
  { id: 'protection', label: 'Protection & journaux', icon: 'shield' },
  { id: 'backups', label: 'Sauvegardes', icon: 'save' },
  { id: 'audit', label: 'Actions administratives', icon: 'history' },
  { id: 'purge', label: 'Suppression des données', icon: 'delete_forever', adminOnly: true },
];

function formatBackupFileDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

export default function Module12SecurityPanel({ user }: { user?: { role?: string } | null }) {
  const [data, setData] = useState<Module12Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [backupFilesPage, setBackupFilesPage] = useState(1);
  const [backupRunning, setBackupRunning] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [restoreFileName, setRestoreFileName] = useState('');
  const [restoreUploadFile, setRestoreUploadFile] = useState<File | null>(null);
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<SecurityTabId>('overview');
  const canPurge = isAdministrateur(user);
  const visibleTabs = SECURITY_TABS.filter((tab) => !tab.adminOnly || canPurge);

  useEffect(() => {
    if (activeTab === 'purge' && !canPurge) {
      setActiveTab('overview');
    }
  }, [activeTab, canPurge]);

  const backupFilesTotal = data?.sauvegardes?.inventaireFichiers?.length ?? 0;

  const showToast = useCallback((message: string, variant: 'success' | 'error' = 'success') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const next = await fetchAdminSecurityModule12({ page: auditPage, limit: AUDIT_PAGE_SIZE });
      setData(next);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Impossible de charger le module sécurité.';
      setLoadError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [auditPage, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(backupFilesTotal / AUDIT_PAGE_SIZE));
    setBackupFilesPage((page) => Math.min(page, maxPage));
  }, [backupFilesTotal]);

  useRegisterPortalRefresh(() => void load());

  const jours = data?.periodeJours ?? 30;
  const vol = data?.volumes30j;
  const auth = data?.authentification;
  const chiffre = data?.chiffrement;
  const backup = data?.sauvegardes;
  const backupFiles = backup?.inventaireFichiers ?? [];
  const backupFilesTotalPages = Math.max(1, Math.ceil(backupFilesTotal / AUDIT_PAGE_SIZE));
  const effectiveBackupFilesPage = Math.min(backupFilesPage, backupFilesTotalPages);
  const backupFilesRangeStart =
    backupFilesTotal === 0 ? 0 : (effectiveBackupFilesPage - 1) * AUDIT_PAGE_SIZE + 1;
  const backupFilesRangeEnd = Math.min(effectiveBackupFilesPage * AUDIT_PAGE_SIZE, backupFilesTotal);
  const backupFilesSummary =
    backupFilesTotal === 0
      ? 'Aucun élément'
      : `Affichage ${backupFilesRangeStart}–${backupFilesRangeEnd} sur ${backupFilesTotal}`;
  const paginatedBackupFiles = backupFiles.slice(
    (effectiveBackupFilesPage - 1) * AUDIT_PAGE_SIZE,
    effectiveBackupFilesPage * AUDIT_PAGE_SIZE,
  );

  const journal = data?.journalisation;
  const roles = data?.roles;
  const auditItems = data?.journalAudit?.items ?? [];
  const auditPagination = data?.journalAudit?.pagination;
  const auditTotal = auditPagination?.total ?? 0;
  const auditTotalPages = auditPagination?.totalPages ?? 1;

  const auditRangeStart = auditTotal === 0 ? 0 : (auditPage - 1) * AUDIT_PAGE_SIZE + 1;
  const auditRangeEnd = Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotal);
  const auditSummary =
    auditTotal === 0
      ? 'Aucun élément'
      : `Affichage ${auditRangeStart}–${auditRangeEnd} sur ${auditTotal}`;

  const handleManualBackup = async () => {
    setBackupRunning(true);
    try {
      await runAdminDatabaseBackup();
      await load();
      showToast('Sauvegarde créée avec succès.');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Sauvegarde impossible.', 'error');
    } finally {
      setBackupRunning(false);
    }
  };

  const handleDownloadBackup = async (fileName: string) => {
    setDownloadingFile(fileName);
    try {
      await downloadAdminDatabaseBackup(fileName);
      showToast(`Fichier « ${fileName} » téléchargé.`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Téléchargement impossible.', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleRestoreBackup = async () => {
    setRestoreRunning(true);
    try {
      let response;
      if (restoreUploadFile) {
        response = await restoreAdminDatabaseBackupFromUpload(restoreUploadFile, restoreConfirmation);
      } else if (restoreFileName) {
        response = await restoreAdminDatabaseBackup({
          fileName: restoreFileName,
          confirmation: restoreConfirmation,
        });
      } else {
        throw new Error('Sélectionnez une copie sur le serveur ou importez un fichier.');
      }
      const stats = response?.data?.stats;
      setRestoreConfirmation('');
      setRestoreUploadFile(null);
      setRestoreFileName('');
      showToast(
        stats
          ? `Restauration réussie : ${stats.utilisateurs} utilisateur(s), ${stats.apprenants} apprenant(s), ${stats.inscriptions} inscription(s).`
          : response?.message || 'Restauration terminée. Les données ont été rechargées depuis la sauvegarde.',
      );
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Restauration impossible.', 'error');
    } finally {
      setRestoreRunning(false);
    }
  };

  return (
    <AdminPageShell
      title="Sécurité & traçabilité"
      description="Vue d’ensemble de la protection de la plateforme, des sauvegardes et du suivi des actions administratives."
      sideCard={{
        label: `Actions suivies (${jours} j)`,
        value: loading ? '…' : String(vol?.entreesAudit ?? 0),
        helper: auth?.jwtSecretsConfigured
          ? `${roles?.administrateursActifs ?? 0} administrateur${(roles?.administrateursActifs ?? 0) > 1 ? 's' : ''} actif${(roles?.administrateursActifs ?? 0) > 1 ? 's' : ''}`
          : 'Configuration de sécurité à finaliser avec l’équipe technique',
        sideLinks: [
          { label: 'Activités apprenants', to: '/admin/activites-apprenants', icon: 'history' },
          { label: 'Activités formateurs', to: '/admin/activites-formateurs', icon: 'cast_for_education' },
        ],
      }}
      isLoading={loading && !data}
      isError={Boolean(loadError) && !data}
      errorMessage={loadError ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement de la sécurité…"
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant} onClose={dismissToast} />

      {data ? (
        <div className="space-y-6">
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiTile
              label={`Actions enregistrées (${jours} j)`}
              value={vol?.entreesAudit ?? 0}
              icon="history"
              tone="primary"
            />
            <KpiTile
              label="Codes de vérification"
              value={vol?.verificationsOtp ?? 0}
              hint={`Sur les ${jours} derniers jours`}
              icon="verified"
              tone="sky"
            />
            <KpiTile
              label="Comptes actifs"
              value={roles?.totalUtilisateurs ?? 0}
              hint={`${roles?.administrateursActifs ?? 0} administrateur(s)`}
              icon="groups"
              tone="emerald"
            />
            <KpiTile
              label="Sauvegardes auto"
              value={backup?.actif ? 'Activées' : 'Désactivées'}
              hint={
                backup?.actif
                  ? formatBackupSchedule(backup.planification)
                  : 'Non configurées sur cette instance'
              }
              icon="save"
              tone="amber"
            />
          </section>

          <div className="academic-panel-tabs" role="tablist" aria-label="Sections sécurité et traçabilité">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`academic-panel-tab inline-flex items-center gap-2 ${
                  activeTab === tab.id ? 'academic-panel-tab--active' : ''
                }`}
              >
                <LucideSymbolIcon name={tab.icon} tone="inherit" size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' ? (
            <section className="grid gap-4 lg:grid-cols-2">
              <AcademicPanelCard
                icon={<LucideSymbolIcon name="shield" tone="primary" size={22} />}
                title="État de la sécurité"
                subtitle="Les six exigences de protection de la plateforme"
              >
                <ul className="space-y-2">
                  {Object.entries(FICHE_LABELS).map(([key, label]) => (
                    <AcademicPanelListItem key={key}>
                      <span className="flex w-full items-center justify-between gap-3">
                        <span className="font-medium text-slate-900">{label}</span>
                        <StatusBadge active={Boolean(data.couvertureFiche?.[key])} />
                      </span>
                    </AcademicPanelListItem>
                  ))}
                </ul>
              </AcademicPanelCard>

              <AcademicPanelCard
                icon={<LucideSymbolIcon name="badge" tone="primary" size={22} />}
                title="Gestion des rôles"
                subtitle="Répartition des comptes par profil Academia"
              >
                <ul className="space-y-2">
                  {(roles?.repartition ?? []).length === 0 ? (
                    <p className="text-sm text-on-surface-variant">Aucun compte enregistré.</p>
                  ) : (
                    roles!.repartition!.map((row) => (
                      <AcademicPanelListItem key={row.role}>
                        <span className="flex w-full items-center justify-between gap-3">
                          <span className="font-medium text-slate-900">{row.label}</span>
                          <span className="text-xs font-bold text-on-surface-variant">
                            {row.count} compte{row.count > 1 ? 's' : ''}
                          </span>
                        </span>
                      </AcademicPanelListItem>
                    ))
                  )}
                </ul>
              </AcademicPanelCard>
            </section>
          ) : null}

          {activeTab === 'protection' ? (
            <section className="grid gap-4 lg:grid-cols-2">
              <AcademicPanelCard
                icon={<LucideSymbolIcon name="lock" tone="primary" size={22} />}
                title="Protection des comptes et des données"
                subtitle="Connexion, mots de passe et échanges sécurisés"
              >
                <ul className="space-y-2 text-sm">
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Sessions de connexion</span>
                        <span className="text-xs text-on-surface-variant">
                          Durée : {formatTokenDuration(auth?.jwtAccessExpiry)} · renouvellement :{' '}
                          {formatTokenDuration(auth?.jwtRefreshExpiry)}
                        </span>
                      </span>
                      <StatusBadge active={Boolean(auth?.jwtSecretsConfigured)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Mots de passe</span>
                        <span className="text-xs text-on-surface-variant">Stockage chiffré et sécurisé</span>
                      </span>
                      <StatusBadge active={Boolean(chiffre?.motsDePasseHash)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Journaux d&apos;activité</span>
                        <span className="text-xs text-on-surface-variant">
                          {chiffre?.algorithmeJournaux ?? 'Détails en clair (clé non configurée)'}
                        </span>
                      </span>
                      <StatusBadge active={Boolean(chiffre?.journalDetailsChiffres)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Vérification en deux étapes</span>
                        <span className="text-xs text-on-surface-variant">
                          Code supplémentaire à la connexion
                        </span>
                      </span>
                      <StatusBadge active={Boolean(auth?.twoFactorEnabled)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Protection contre les intrusions web</span>
                        <span className="text-xs text-on-surface-variant">Filtres de sécurité actifs</span>
                      </span>
                      <StatusBadge active={Boolean(auth?.helmetActif)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span>
                        <span className="block font-medium text-slate-900">Échanges sécurisés</span>
                        <span className="text-xs text-on-surface-variant">
                          {chiffre?.transitHttpsRecommande
                            ? 'Connexion protégée (HTTPS)'
                            : 'À renforcer en production'}
                        </span>
                      </span>
                      <StatusBadge active={Boolean(chiffre?.transitHttpsRecommande)} />
                    </span>
                  </AcademicPanelListItem>
                </ul>
              </AcademicPanelCard>

              <AcademicPanelCard
                icon={<LucideSymbolIcon name="description" tone="primary" size={22} />}
                title="Enregistrement des activités"
                subtitle="Historique technique conservé pour le suivi et le dépannage"
                badge={<StatusBadge active={Boolean(journal?.console)} />}
              >
                <ul className="space-y-2 text-sm">
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="font-medium text-slate-900">Suivi en temps réel</span>
                      <StatusBadge active={Boolean(journal?.console)} />
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="font-medium text-slate-900">Archivage sur disque</span>
                      <StatusBadge active={Boolean(journal?.fichier)} />
                    </span>
                  </AcademicPanelListItem>
                </ul>
              </AcademicPanelCard>
            </section>
          ) : null}

          {activeTab === 'backups' ? (
            <section className="space-y-4">
              <AcademicPanelCard
                icon={<LucideSymbolIcon name="save" tone="primary" size={22} />}
                title="Sauvegardes automatiques"
                subtitle="Copies de la base de données et durée de conservation"
                badge={<StatusBadge active={Boolean(backup?.actif)} />}
              >
                <div className="space-y-3 text-sm text-on-surface-variant">
                  <p>{backup?.message}</p>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="font-medium text-slate-900">Fréquence</span>
                      <span className="text-xs font-bold">
                        {formatBackupSchedule(backup?.planification)}
                      </span>
                    </span>
                  </AcademicPanelListItem>
                  <AcademicPanelListItem>
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="font-medium text-slate-900">Conservation</span>
                      <span className="text-xs font-bold">{backup?.retentionJours ?? 14} jours</span>
                    </span>
                  </AcademicPanelListItem>
                  {backup?.derniereReussite ? (
                    <AcademicPanelListItem>
                      <span className="flex w-full items-center justify-between gap-3">
                        <span className="font-medium text-slate-900">Dernière sauvegarde</span>
                        <span className="text-xs font-bold">
                          {new Date(backup.derniereReussite).toLocaleString('fr-FR')}
                        </span>
                      </span>
                    </AcademicPanelListItem>
                  ) : null}
                  {typeof backup?.fichiers === 'number' ? (
                    <AcademicPanelListItem>
                      <span className="flex w-full items-center justify-between gap-3">
                        <span className="font-medium text-slate-900">Copies disponibles</span>
                        <span className="text-xs font-bold">{backup.fichiers}</span>
                      </span>
                    </AcademicPanelListItem>
                  ) : null}
                  {backup?.derniereErreur ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      La dernière tentative de sauvegarde a échoué. Réessayez ou contactez le support technique.
                    </p>
                  ) : null}
                  <button
                    type="button"
                    disabled={backupRunning}
                    onClick={() => void handleManualBackup()}
                    className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-semibold text-primary transition hover:bg-primary/10 disabled:opacity-50"
                  >
                    <LucideSymbolIcon name="save" tone="inherit" size={16} />
                    {backupRunning ? 'Sauvegarde en cours…' : 'Créer une sauvegarde maintenant'}
                  </button>
                </div>
              </AcademicPanelCard>

              <AcademicPanelCard
                flush
                icon={<LucideSymbolIcon name="save" tone="primary" size={22} />}
                title="Sauvegarde & restauration"
                subtitle="Téléchargez une copie de la base ou restaurez les données depuis un fichier"
              >
            <div className="space-y-4 p-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <p className="font-semibold">Opération sensible</p>
                <p className="mt-1 text-xs leading-relaxed">
                  La restauration remplace le contenu actuel de la base de données. Une sauvegarde de
                  sécurité est créée automatiquement avant toute restauration. Choisissez une copie récente
                  créée après vos saisies (évitez les toutes premières copies du démarrage). Réservé aux
                  administrateurs.
                </p>
              </div>

              <div>
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Copies disponibles sur le serveur</h3>
                  <p className="mt-1 text-xs text-on-surface-variant">
                    Téléchargez un fichier .sql.gz pour le conserver hors plateforme.
                  </p>
                </div>
                <AcademicPanelTableWrap
                  empty={
                    backupFiles.length === 0 ? (
                      <p className="text-sm text-on-surface-variant">
                        Aucune copie disponible. Créez d&apos;abord une sauvegarde.
                      </p>
                    ) : undefined
                  }
                >
                  {backupFiles.length > 0 ? (
                    <>
                      <table className="academic-panel-table">
                        <thead>
                          <tr>
                            <th>Fichier</th>
                            <th className="hidden sm:table-cell">Taille</th>
                            <th className="hidden md:table-cell">Date</th>
                            <th className="text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedBackupFiles.map((file) => (
                            <tr key={file.nom}>
                              <td className="max-w-[12rem] truncate font-medium text-slate-900 sm:max-w-none">
                                {file.nom}
                              </td>
                              <td className="hidden sm:table-cell text-xs text-on-surface-variant">
                                {file.tailleLisible ?? '—'}
                              </td>
                              <td className="hidden md:table-cell text-xs text-on-surface-variant">
                                {formatBackupFileDate(file.dateModification)}
                              </td>
                              <td className="text-right">
                                <div className="flex flex-wrap justify-end gap-2">
                                  <button
                                    type="button"
                                    disabled={downloadingFile === file.nom}
                                    onClick={() => void handleDownloadBackup(file.nom)}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10 disabled:opacity-50"
                                  >
                                    <LucideSymbolIcon name="download" tone="inherit" size={16} />
                                    {downloadingFile === file.nom ? 'Téléchargement…' : 'Télécharger'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={restoreRunning}
                                    onClick={() => {
                                      setRestoreFileName(file.nom);
                                      setRestoreUploadFile(null);
                                    }}
                                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition disabled:opacity-50 ${
                                      restoreFileName === file.nom && !restoreUploadFile
                                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                                        : 'border-outline-variant/30 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    <LucideSymbolIcon name="refresh" tone="inherit" size={16} />
                                    Restaurer
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <AdminPagination
                        className="border-t border-outline-variant/15 px-4 py-3"
                        currentPage={effectiveBackupFilesPage}
                        totalPages={backupFilesTotalPages}
                        onPageChange={setBackupFilesPage}
                        summary={backupFilesSummary}
                      />
                    </>
                  ) : null}
                </AcademicPanelTableWrap>
              </div>

              <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/40 p-4">
                <h3 className="text-sm font-bold text-slate-900">Restaurer depuis un fichier externe</h3>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Importez une sauvegarde .sql ou .sql.gz précédemment téléchargée.
                </p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="file"
                    accept=".sql,.gz,application/gzip,application/sql"
                    disabled={restoreRunning}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      setRestoreUploadFile(file);
                      if (file) setRestoreFileName('');
                    }}
                    className="block w-full text-sm text-on-surface-variant file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-primary"
                  />
                  {restoreUploadFile ? (
                    <span className="text-xs font-medium text-slate-700">{restoreUploadFile.name}</span>
                  ) : null}
                </div>

                <div className="mt-4 space-y-2">
                  <label htmlFor="restore-confirmation" className="text-xs font-semibold text-slate-900">
                    Confirmation requise
                  </label>
                  <input
                    id="restore-confirmation"
                    type="text"
                    value={restoreConfirmation}
                    disabled={restoreRunning}
                    onChange={(event) => setRestoreConfirmation(event.target.value)}
                    placeholder={`Saisissez ${RESTORE_CONFIRMATION} pour confirmer`}
                    className="w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm"
                  />
                  {(restoreFileName || restoreUploadFile) && restoreConfirmation === RESTORE_CONFIRMATION ? (
                    <p className="text-xs text-amber-800">
                      Source : {restoreUploadFile ? restoreUploadFile.name : restoreFileName}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={
                    restoreRunning ||
                    restoreConfirmation !== RESTORE_CONFIRMATION ||
                    (!restoreFileName && !restoreUploadFile)
                  }
                  onClick={() => void handleRestoreBackup()}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800 transition hover:bg-red-100 disabled:opacity-50"
                >
                  <LucideSymbolIcon name="warning" tone="inherit" size={16} />
                  {restoreRunning ? 'Restauration en cours…' : 'Lancer la restauration'}
                </button>
              </div>
              </div>
            </AcademicPanelCard>
            </section>
          ) : null}

          {activeTab === 'audit' ? (
            <AcademicPanelCard
              flush
              icon={<LucideSymbolIcon name="history" tone="primary" size={22} />}
              title="Suivi des actions administratives"
              subtitle="Historique des opérations réalisées sur la plateforme"
            >
              <AcademicPanelTableWrap
                empty={
                  auditItems.length === 0 ? (
                    <p className="text-sm text-on-surface-variant">Aucune action enregistrée pour l&apos;instant.</p>
                  ) : undefined
                }
              >
                {auditItems.length > 0 ? (
                  <>
                    <table className="academic-panel-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Acteur</th>
                          <th>Opération</th>
                          <th className="hidden md:table-cell">Détails</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditItems.map((row) => (
                          <tr key={row.id}>
                            <td className="whitespace-nowrap text-xs text-on-surface-variant">
                              {formatShortDate(row.when)}
                            </td>
                            <td className="font-semibold text-slate-900">{row.who}</td>
                            <td className={row.danger ? 'font-semibold text-red-700' : 'text-slate-800'}>
                              {row.action}
                            </td>
                            <td className="hidden max-w-xs truncate md:table-cell text-xs text-on-surface-variant">
                              {formatAuditDetailsPreview(row.details)}
                            </td>
                            <td className="text-right">
                              <Link
                                to={`/admin/security/actions/${row.id}`}
                                state={{ actionLabel: row.action }}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10"
                                title="Voir le détail complet"
                              >
                                <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                                Voir
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <AdminPagination
                      className="border-t border-outline-variant/15 px-4 py-3"
                      currentPage={auditPage}
                      totalPages={auditTotalPages}
                      onPageChange={setAuditPage}
                      summary={auditSummary}
                    />
                  </>
                ) : null}
              </AcademicPanelTableWrap>
            </AcademicPanelCard>
          ) : null}

          {activeTab === 'purge' && canPurge ? <Module12DataPurgeSection onToast={showToast} /> : null}
        </div>
      ) : null}
    </AdminPageShell>
  );
}
